import { useState } from "react";
import axios from "axios";
import { ShieldCheck, RefreshCw, LogOut, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
    const [key, setKey] = useState(sessionStorage.getItem("amx_admin_key") || "");
    const [enquiries, setEnquiries] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async (k) => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API}/admin/enquiries`, {
                headers: { "X-Admin-Key": k },
            });
            setEnquiries(res.data.enquiries);
            sessionStorage.setItem("amx_admin_key", k);
        } catch (e) {
            setEnquiries(null);
            setError(
                e.response?.status === 401
                    ? "Invalid admin key. Please check and try again."
                    : "Could not load enquiries. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem("amx_admin_key");
        setKey("");
        setEnquiries(null);
    };

    return (
        <div className="min-h-screen bg-[#07090E] text-slate-100 px-4 sm:px-6 py-10">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <img src="/assets/logo-mark.png" alt="Animexa" className="h-10 w-10 rounded-md" />
                    <div>
                        <p className="font-display font-extrabold text-white leading-none">ANIMEXA</p>
                        <p className="font-jbmono text-[9px] tracking-[0.3em] text-[#F5C518] mt-1">
                            ENQUIRIES — ADMIN ONLY
                        </p>
                    </div>
                </div>

                {enquiries === null ? (
                    <div className="max-w-sm mx-auto mt-16 rounded-3xl border border-white/10 bg-[#0D131F] p-8">
                        <ShieldCheck size={28} className="text-[#F5C518] mb-4" />
                        <h1 className="font-display text-xl font-bold text-white">Admin Access</h1>
                        <p className="mt-2 text-sm text-slate-400">
                            Enter the admin key to view saved enquiries.
                        </p>
                        <input
                            data-testid="admin-key-input"
                            type="password"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            placeholder="Admin key"
                            className="mt-5 w-full rounded-xl border border-white/10 bg-[#0A0E17] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#F5C518]/70"
                        />
                        {error && (
                            <p data-testid="admin-error" className="mt-2 text-xs text-red-400">
                                {error}
                            </p>
                        )}
                        <button
                            data-testid="admin-login-btn"
                            onClick={() => load(key)}
                            disabled={loading || !key}
                            className="mt-5 w-full rounded-full bg-[#F5C518] px-6 py-3 text-sm font-semibold text-[#07090E] transition-colors hover:bg-[#E5B50F] disabled:opacity-60"
                        >
                            {loading ? "Checking…" : "View Enquiries"}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-sm text-slate-400" data-testid="admin-count">
                                {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"} saved
                            </p>
                            <div className="flex gap-2">
                                <button
                                    data-testid="admin-refresh-btn"
                                    onClick={() => load(key)}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-[#00E5FF]/40 px-4 py-2 text-xs text-[#00E5FF] hover:bg-[#00E5FF]/10"
                                >
                                    <RefreshCw size={13} /> Refresh
                                </button>
                                <button
                                    data-testid="admin-logout-btn"
                                    onClick={logout}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs text-slate-400 hover:text-white"
                                >
                                    <LogOut size={13} /> Exit
                                </button>
                            </div>
                        </div>
                        {enquiries.length === 0 ? (
                            <div className="rounded-3xl border border-white/10 bg-[#0D131F] p-12 text-center">
                                <Inbox size={28} className="mx-auto text-slate-600 mb-3" />
                                <p className="text-sm text-slate-500">No enquiries yet.</p>
                            </div>
                        ) : (
                            <div data-testid="admin-enquiries-list" className="space-y-4">
                                {enquiries.map((q, i) => (
                                    <div
                                        key={q.id || i}
                                        data-testid={`admin-enquiry-row-${i}`}
                                        className="rounded-2xl border border-white/8 bg-[#0D131F] p-5"
                                    >
                                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                                            <p className="font-display text-base font-semibold text-white">
                                                {q.name}
                                                {q.business_name && (
                                                    <span className="text-slate-500 font-normal text-sm">
                                                        {" "}
                                                        · {q.business_name}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="font-jbmono text-[10px] text-slate-500">
                                                {new Date(q.created_at).toLocaleString("en-IN")}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                                            <p className="text-slate-400">
                                                <span className="text-slate-600 text-xs uppercase tracking-wider">Mobile: </span>
                                                {q.phone}
                                            </p>
                                            <p className="text-slate-400 break-all">
                                                <span className="text-slate-600 text-xs uppercase tracking-wider">Email: </span>
                                                {q.email}
                                            </p>
                                            <p className="text-slate-400">
                                                <span className="text-slate-600 text-xs uppercase tracking-wider">Type: </span>
                                                {q.business_type}
                                            </p>
                                            <p className="text-slate-400">
                                                <span className="text-slate-600 text-xs uppercase tracking-wider">Service: </span>
                                                <span className="text-[#F5C518]">{q.service}</span>
                                            </p>
                                        </div>
                                        <p className="mt-3 rounded-xl border border-white/8 bg-[#0A0E17] px-4 py-3 text-sm text-slate-400 leading-relaxed">
                                            {q.details}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
